import React, { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  ListIcon,
  Divider,
  Progress,
  useToast
} from '@chakra-ui/react';
import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import CardHeader from "../../../../../components/Card/CardHeader.js";
import {
  FiCheck,
  FiX,
  FiDownload,
  FiRefreshCw,
  FiCreditCard,
  FiUser,
  FiTrendingUp,
  FiAlertTriangle,
  FiFileText,
  FiCalendar
} from 'react-icons/fi';

const SyncResults = ({ syncResult, comparisonResult, onExportReport }) => {
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const successColor = useColorModeValue('green.50', 'green.900');
  const errorColor = useColorModeValue('red.50', 'red.900');

  if (!syncResult) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription>
          No hay resultados de sincronización disponibles.
        </AlertDescription>
      </Alert>
    );
  }

  const totalActions = syncResult.statistics.totalProcessed;
  const successActions = syncResult.statistics.ordersUpdated + syncResult.statistics.ordersCreated;
  const errorActions = syncResult.statistics.errors;
  const successRate = totalActions > 0 ? (successActions / totalActions) * 100 : 0;

  const exportReport = async () => {
    setIsExporting(true);
    
    try {
      // Crear reporte detallado
      const report = {
        timestamp: new Date().toISOString(),
        summary: {
          totalProcessed: totalActions,
          successful: successActions,
          failed: errorActions,
          successRate: successRate.toFixed(1) + '%'
        },
        statistics: syncResult.statistics,
        successful: syncResult.success,
        failed: syncResult.failed,
        originalComparison: {
          totalNapsis: comparisonResult?.statistics.totalNapsis || 0,
          totalMatched: comparisonResult?.statistics.totalMatched || 0,
          totalDiscrepancies: comparisonResult?.statistics.totalDiscrepancies || 0,
          notFound: comparisonResult?.notFound?.length || 0
        }
      };
      
      // Convertir a JSON para descarga
      const jsonString = JSON.stringify(report, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `sync-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      if (onExportReport) {
        onExportReport();
      }
      
    } catch (error) {
      console.error('Error exportando reporte:', error);
      toast({
        title: "Error exportando reporte",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-CL');
  };

  // Si hubo un error general
  if (syncResult.error) {
    return (
      <VStack spacing={6} align="stretch">
        <Alert status="error">
          <AlertIcon />
          <VStack align="start" spacing={2} flex={1}>
            <AlertTitle>Error en la sincronización</AlertTitle>
            <AlertDescription>
              {syncResult.error}
            </AlertDescription>
          </VStack>
        </Alert>
        
        <Button
          leftIcon={<Icon as={FiDownload} />}
          onClick={exportReport}
          isLoading={isExporting}
          loadingText="Exportando..."
        >
          Descargar Reporte de Error
        </Button>
      </VStack>
    );
  }

  // Si no hubo nada que sincronizar
  if (totalActions === 0) {
    return (
      <VStack spacing={6} align="stretch">
        <Alert status="success">
          <AlertIcon />
          <VStack align="start" spacing={2} flex={1}>
            <AlertTitle>Sincronización no requerida</AlertTitle>
            <AlertDescription>
              Todos los datos de NAPSIS ya están sincronizados con el sistema.
              No se encontraron diferencias que requieran actualización.
            </AlertDescription>
          </VStack>
        </Alert>
        
        <Card>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Stat>
                <StatLabel>Estudiantes NAPSIS</StatLabel>
                <StatNumber color="blue.500">
                  {comparisonResult?.statistics.totalNapsis || 0}
                </StatNumber>
                <StatHelpText>Total en archivo</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Coincidencias</StatLabel>
                <StatNumber color="green.500">
                  {comparisonResult?.statistics.totalMatched || 0}
                </StatNumber>
                <StatHelpText>Usuarios encontrados</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Discrepancias</StatLabel>
                <StatNumber color="orange.500">
                  {comparisonResult?.statistics.totalDiscrepancies || 0}
                </StatNumber>
                <StatHelpText>Sin impacto</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Estado</StatLabel>
                <StatNumber color="green.500">100%</StatNumber>
                <StatHelpText>Sincronizado</StatHelpText>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      
      {/* Resumen general */}
      <Card>
        <CardHeader>
          <HStack spacing={3}>
            <Icon 
              as={errorActions > 0 ? FiAlertTriangle : FiCheck} 
              color={errorActions > 0 ? "orange.500" : "green.500"} 
              boxSize={6} 
            />
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="semibold">
                Sincronización {errorActions > 0 ? 'Completada con Advertencias' : 'Exitosa'}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {formatDate(new Date().toISOString())}
              </Text>
            </VStack>
          </HStack>
        </CardHeader>
        
        <CardBody>
          <VStack spacing={4} align="stretch">
            
            {/* Barra de progreso general */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium">
                  Tasa de éxito: {successRate.toFixed(1)}%
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {successActions} de {totalActions} acciones exitosas
                </Text>
              </HStack>
              <Progress 
                value={successRate} 
                colorScheme={successRate === 100 ? "green" : "orange"} 
                size="lg" 
                borderRadius="md"
              />
            </VStack>
            
            {/* Estadísticas en grid */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Stat>
                <StatLabel>Órdenes Actualizadas</StatLabel>
                <StatNumber color="orange.500">
                  {syncResult.statistics.ordersUpdated}
                </StatNumber>
                <StatHelpText>
                  <Icon as={FiRefreshCw} mr={1} />
                  Estado cambiado
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Órdenes Creadas</StatLabel>
                <StatNumber color="blue.500">
                  {syncResult.statistics.ordersCreated}
                </StatNumber>
                <StatHelpText>
                  <Icon as={FiCreditCard} mr={1} />
                  Nuevas órdenes
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Total Procesado</StatLabel>
                <StatNumber color="green.500">
                  {totalActions}
                </StatNumber>
                <StatHelpText>
                  <Icon as={FiTrendingUp} mr={1} />
                  Acciones ejecutadas
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Errores</StatLabel>
                <StatNumber color={errorActions > 0 ? "red.500" : "green.500"}>
                  {errorActions}
                </StatNumber>
                <StatHelpText>
                  <Icon as={errorActions > 0 ? FiX : FiCheck} mr={1} />
                  {errorActions > 0 ? 'Con problemas' : 'Sin errores'}
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Detalles en tabs */}
      <Tabs variant="enclosed">
        <TabList>
          <Tab>
            <HStack spacing={2}>
              <Icon as={FiCheck} />
              <Text>Exitosas ({syncResult.success.length})</Text>
            </HStack>
          </Tab>
          {syncResult.failed.length > 0 && (
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiX} />
                <Text>Errores ({syncResult.failed.length})</Text>
              </HStack>
            </Tab>
          )}
          <Tab>
            <HStack spacing={2}>
              <Icon as={FiFileText} />
              <Text>Resumen</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          
          {/* Panel de acciones exitosas */}
          <TabPanel px={0}>
            {syncResult.success.length > 0 ? (
              <VStack spacing={4} align="stretch">
                
                {syncResult.success.map((item, index) => (
                  <Card key={index} bg={successColor} border="1px" borderColor="green.200">
                    <CardBody py={3}>
                      <HStack justify="space-between" align="start">
                        <HStack spacing={3}>
                          <Icon 
                            as={item.type === 'update' ? FiRefreshCw : FiCreditCard} 
                            color="green.500" 
                            boxSize={5} 
                          />
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" fontSize="sm">
                              {item.customer.first_name} {item.customer.last_name}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {item.message}
                            </Text>
                            {item.order_id && (
                              <Text fontSize="xs" color="gray.500">
                                Orden ID: {item.order_id}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                        
                        <Badge 
                          colorScheme="green" 
                          variant="solid"
                          textTransform="capitalize"
                        >
                          {item.type === 'update' ? 'Actualizada' : 'Creada'}
                        </Badge>
                      </HStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            ) : (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  No hay acciones exitosas para mostrar.
                </AlertDescription>
              </Alert>
            )}
          </TabPanel>

          {/* Panel de errores */}
          {syncResult.failed.length > 0 && (
            <TabPanel px={0}>
              <VStack spacing={4} align="stretch">
                
                <Alert status="error" variant="left-accent">
                  <AlertIcon />
                  <AlertDescription>
                    Los siguientes elementos no pudieron ser sincronizados:
                  </AlertDescription>
                </Alert>
                
                {syncResult.failed.map((item, index) => (
                  <Card key={index} bg={errorColor} border="1px" borderColor="red.200">
                    <CardBody py={3}>
                      <HStack justify="space-between" align="start">
                        <HStack spacing={3}>
                          <Icon as={FiX} color="red.500" boxSize={5} />
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" fontSize="sm">
                              {item.customer.first_name} {item.customer.last_name}
                            </Text>
                            <Text fontSize="xs" color="red.600">
                              {item.error}
                            </Text>
                            {item.action && (
                              <Text fontSize="xs" color="gray.500">
                                Acción: {item.action.action} - {item.action.mes || 'N/A'}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                        
                        <Badge colorScheme="red" variant="solid">
                          Error
                        </Badge>
                      </HStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            </TabPanel>
          )}

          {/* Panel de resumen */}
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              
              <Card>
                <CardHeader>
                  <Text fontSize="md" fontWeight="semibold">
                    Resumen de la Operación
                  </Text>
                </CardHeader>
                <CardBody>
                  <List spacing={3}>
                    <ListItem>
                      <ListIcon as={FiCalendar} color="blue.500" />
                      <strong>Fecha:</strong> {formatDate(new Date().toISOString())}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiUser} color="blue.500" />
                      <strong>Estudiantes procesados:</strong> {comparisonResult?.statistics.totalMatched || 0}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiCreditCard} color="blue.500" />
                      <strong>Órdenes afectadas:</strong> {successActions}
                    </ListItem>
                    <ListItem>
                      <ListIcon as={FiTrendingUp} color="blue.500" />
                      <strong>Tasa de éxito:</strong> {successRate.toFixed(1)}%
                    </ListItem>
                    {errorActions > 0 && (
                      <ListItem>
                        <ListIcon as={FiAlertTriangle} color="orange.500" />
                        <strong>Elementos con errores:</strong> {errorActions}
                      </ListItem>
                    )}
                  </List>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <Text fontSize="md" fontWeight="semibold">
                    Comparación Original
                  </Text>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Datos NAPSIS:
                      </Text>
                      <List spacing={1} fontSize="sm">
                        <ListItem>
                          <strong>{comparisonResult?.statistics.totalNapsis || 0}</strong> estudiantes
                        </ListItem>
                        <ListItem>
                          <strong>{comparisonResult?.statistics.totalMatched || 0}</strong> coincidencias encontradas
                        </ListItem>
                        <ListItem>
                          <strong>{comparisonResult?.notFound?.length || 0}</strong> no encontrados
                        </ListItem>
                      </List>
                    </VStack>
                    
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Acciones requeridas:
                      </Text>
                      <List spacing={1} fontSize="sm">
                        <ListItem>
                          <strong>{comparisonResult?.statistics.pendingToComplete || 0}</strong> actualizaciones de estado
                        </ListItem>
                        <ListItem>
                          <strong>{comparisonResult?.statistics.missingOrders || 0}</strong> órdenes a crear
                        </ListItem>
                        <ListItem>
                          <strong>{comparisonResult?.statistics.totalDiscrepancies || 0}</strong> discrepancias totales
                        </ListItem>
                      </List>
                    </VStack>
                  </SimpleGrid>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Botón de exportar reporte */}
      <Divider />
      <HStack justify="center">
        <Button
          leftIcon={<Icon as={FiDownload} />}
          onClick={exportReport}
          isLoading={isExporting}
          loadingText="Exportando..."
          variant="outline"
          colorScheme="blue"
        >
          Descargar Reporte Completo
        </Button>
      </HStack>
    </VStack>
  );
};

export default SyncResults;